import React from 'react'

import {
  PaneRadioInput,
  PaneTypeCard,
  PaneTypeCardBody,
  PaneTypeCardDescription,
  PaneTypeCardHeader,
  PaneTypeCardLabel,
} from 'features/charts-v2/components/filter-popover-pane.styles'
import { type SentimentMode } from 'features/charts-v2/components/SentimentFilterCard'

const SENTIMENT_TYPE_OPTIONS: Array<{
  value: SentimentMode
  label: string
  description: string
}> = [
  {
    value: 'sentence',
    label: 'Sentence Sentiment',
    description: 'Filters based on the sentiment of individual sentences.',
  },
  {
    value: 'record',
    label: 'Review Sentiment',
    description:
      'Filters based on the overall sentiment score of the full review.',
  },
]

interface SentimentTypeSelectorProps {
  value: SentimentMode
  onChange: (value: SentimentMode) => void
  // Restricts which modes may be NEWLY selected — e.g. a dashboard global
  // Sentiment filter only constrains one of sentence/record sentiment.
  // Undefined = unrestricted. Mirrors `RatingTypeSelector`'s `allowedValues`:
  // the currently-selected mode stays visible and selectable even when
  // disallowed, so the user can see and change it.
  allowedModes?: readonly SentimentMode[]
}

// The two "Type" radio cards (Sentence Sentiment / Review Sentiment). Pure
// value/onChange, same shape as RatingTypeSelector minus the preview images
// (Sentiment's type cards don't have any).
export const SentimentTypeSelector: React.FC<SentimentTypeSelectorProps> = ({
  value,
  onChange,
  allowedModes,
}) => (
  <>
    {SENTIMENT_TYPE_OPTIONS.map((option) => {
      const isSelected = value === option.value
      const isDisabled =
        !isSelected &&
        allowedModes !== undefined &&
        !allowedModes.includes(option.value)
      return (
        <PaneTypeCard
          key={option.value}
          $selected={isSelected}
          $disabled={isDisabled}
          onClick={() => !isDisabled && onChange(option.value)}
        >
          <PaneTypeCardHeader>
            <PaneRadioInput
              type="radio"
              checked={isSelected}
              disabled={isDisabled}
              onChange={() => !isDisabled && onChange(option.value)}
            />
            <PaneTypeCardLabel $selected={isSelected}>
              {option.label}
            </PaneTypeCardLabel>
          </PaneTypeCardHeader>
          <PaneTypeCardBody>
            <PaneTypeCardDescription>
              {option.description}
            </PaneTypeCardDescription>
          </PaneTypeCardBody>
        </PaneTypeCard>
      )
    })}
  </>
)
